import * as Sentry from "@sentry/vue";

import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { onMounted, ref, watch } from "vue";

import { useStekManager } from "@/composables/stek-manager";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { type DomainInTree } from "@/stores/domains-store";
import { type ProcessInTree } from "@/stores/processes-store";
import { type ProductInTree } from "@/stores/products-store";
import { useReleaseStore } from "@/stores/release-store";
import { type SyncReturn, useSyncStore } from "@/stores/sync-store";

export type Worktree = types.Worktree;

// The worktree store is primarily used for the lefthand stekz-tree component.
// However, it stores quite a bit of information which is useful for other pages
// that want to use some meta info (i.e. what is the current domain identity)
// without having those pages to actually fetch the entire entity from the api.
export const useWorktreeStore = defineStore("worktree", () => {
    const worktree = ref<Worktree>();
    const isFetching = ref(false);
    const { stek } = useStekManager();
    const toast = useToast();

    const controller = ref(new AbortController());

    const handleSyncError = (error: unknown, context: string) => {
        const errorMessage =
            error instanceof Error
                ? error.message
                : typeof error === "string"
                  ? error
                  : "Failed to sync worktree";

        toast.add({
            severity: "error",
            summary: "Sync Error",
            detail: `Failed to load ${context}: ${errorMessage}`,
        });
        Sentry.captureException(error);
        if (context !== "tree of the stek") {
            console.error("Error syncing worktree:", error);
        }
        isFetching.value = false;
    };

    const sync = async () => {
        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        isFetching.value = true;
        try {
            const revision = useReleaseStore().activeRevision;
            const { response, data } =
                await api.startWorkspaceWorktreeMgtGetWorktreeStartPost({
                    body: {
                        workspace_id: "default",
                        revision: revision ?? undefined,
                    },
                    signal: controller.value.signal,
                });

            if (!response.ok) {
                handleSyncError(
                    response.statusText || "Failed to sync worktree",
                    "tree of the stek"
                );
                return;
            }

            worktree.value = data;
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                handleSyncError(error, "workspace structure");
            }
        } finally {
            isFetching.value = false;
        }
    };

    watch(
        () => stek.value?.identity,
        () => {
            if (stek.value) {
                sync();
            } else {
                worktree.value = undefined;
            }
        }
    );

    function findDomainByIdentity(identity: string): DomainInTree | undefined {
        if (!worktree.value) {
            return;
        }

        for (const domain of worktree.value.domains) {
            if (domain.identity === identity) {
                return domain;
            }
        }
    }

    function findProductByIdentity(
        identity: string
    ): ProductInTree | undefined {
        if (!worktree.value) {
            return;
        }

        for (const domain of worktree.value.domains) {
            for (const product of domain.products) {
                if (product.identity === identity) {
                    return product;
                }
            }
        }
    }

    function findProcessByIdentity(
        identity: string
    ): ProcessInTree | undefined {
        if (!worktree.value) {
            return;
        }

        for (const domain of worktree.value.domains) {
            for (const product of domain.products) {
                for (const process of product.processes) {
                    if (process.identity === identity) {
                        return process;
                    }
                }
            }
        }
    }

    onMounted(() => {
        const syncStore = useSyncStore();
        const releaseStore = useReleaseStore();
        const { sync } = useWorktreeStore();

        // Re-sync when active release/revision changes
        watch(
            () => releaseStore.activeRevision,
            () => {
                if (stek.value) {
                    sync();
                }
            }
        );

        // Listen to changes on the current store.
        syncStore.$onAction(({ name, after }) => {
            if (name === "sync") {
                after((fullySyncedIdentities: SyncReturn) => {
                    if (
                        fullySyncedIdentities.some(
                            (identity) =>
                                identity.attribute === "name" ||
                                identity.attribute === "description"
                        )
                    ) {
                        if (stek.value) {
                            sync();
                        }
                    }
                });
            }
        });

        // Listen to changes of stores in other tabs.
        syncChannel.addEventListener(
            "message",
            ({ data: { message, identity } }) => {
                if (
                    message === "synced" &&
                    (identity.attribute === "name" ||
                        identity.attribute === "description")
                ) {
                    if (stek.value) {
                        sync();
                    }
                }
            }
        );
    });

    return {
        worktree,
        sync,
        isFetching,
        findDomainByIdentity,
        findProductByIdentity,
        findProcessByIdentity,
    };
});

export const useWorktreeManager = () => storeToRefs(useWorktreeStore());
